/**
 * レセプト内容CSV出力 — POST /export-csv → blob → synthetic <a download> click.
 * Reactive to serviceType (A2 ↔ R2). Filename comes from BE Content-Disposition;
 * fallback derives from rezept_kbn (matches BE template `CSVレセプト{Ymd}.csv` / `CSV医療助成レセプト{Ymd}.csv`).
 */

import { useMutation } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import useAppToast from '@/hooks/useAppToast';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { CSV_ERROR_MESSAGE, CSV_SUCCESS_MESSAGE } from '@/constants/messages';
import { getApiErrorMessageAsync } from '@/utils/apiError';
import { exportRezeptHistoriesCsv } from '@/services/main/rezeptHistories';
import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import {
  REZEPT_KBN_MAIN,
  REZEPT_KBN_RELATED,
} from '@/pages/main/rezeptHistories/constants';
import type {
  RezeptHistoriesExportCsvPayload,
  RezeptKbn,
} from '@/pages/main/rezeptHistories/types';
import {
  downloadBlob,
  formatYmd,
  parseFilenameFromHeader,
} from '@/utils/blobDownload';

const FILENAME_PREFIX_KBN1 = 'CSVレセプト';
const FILENAME_PREFIX_KBN2 = 'CSV医療助成レセプト';
const FILENAME_EXTENSION = '.csv';

/** Filename prefix per レセプト種別; mirrors BE `RezeptHistoryConstants::FILENAME_KBN{1,2}_TPL`. */
const FILENAME_PREFIX_BY_KBN: Record<RezeptKbn, string> = {
  [REZEPT_KBN_MAIN]: FILENAME_PREFIX_KBN1,
  [REZEPT_KBN_RELATED]: FILENAME_PREFIX_KBN2,
};

function fallbackFilename(rezeptKbn: RezeptKbn): string {
  const prefix = FILENAME_PREFIX_BY_KBN[rezeptKbn] ?? FILENAME_PREFIX_KBN1;
  return `${prefix}${formatYmd()}${FILENAME_EXTENSION}`;
}

export function useExportRezeptHistoriesCsvMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  // Custom mutation: we need the AxiosResponse to read Content-Disposition.
  // Service helper is reused for the success-only path; here we need full response.
  const { mutateAsync, isPending: isLoading } = useMutation<
    AxiosResponse<Blob>,
    Error,
    RezeptHistoriesExportCsvPayload
  >({
    mutationFn: payload =>
      apiClient.post<Blob>(
        clinicApiPath(tenant, clinicNumber, '/rezept-histories/export-csv'),
        payload,
        {
          responseType: 'blob',
          headers: { 'X-Service-Type': serviceType },
        }
      ),
    onSuccess: () => {
      toast.success(CSV_SUCCESS_MESSAGE);
    },
    onError: async error => {
      // responseType:'blob' → axios leaves error body as a Blob; parse it before toasting
      // so BE messages like '該当するデータはありませんでした。' surface to the user.
      toast.error(await getApiErrorMessageAsync(error, CSV_ERROR_MESSAGE));
    },
  });

  const triggerExport = async (payload: RezeptHistoriesExportCsvPayload) => {
    try {
      const response = await mutateAsync(payload);
      const filename =
        parseFilenameFromHeader(response.headers?.['content-disposition']) ??
        fallbackFilename(payload.rezept_kbn);
      downloadBlob(response.data, filename);
    } catch {
      // onError already surfaced the toast.
    }
  };

  return { triggerExport, isLoading };
}

// Re-exported so the page can mock/inspect the service in tests if needed.
export { exportRezeptHistoriesCsv };
